import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { XMarkIcon, PlayIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { payrollService } from '../../../services/accounting/payroll';
import { formatCurrency } from '../../../utils/formatting';
import toast from 'react-hot-toast';

/**
 * ProcessPayrollModal Component
 * 
 * Modal for processing payroll accrual with preview and confirmation
 */
const ProcessPayrollModal = ({ isOpen, onClose, onSuccess, period }) => {
  const { t } = useTranslation();
  const { currentCompany, currentUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState('preview'); // preview, confirm, processing
  const [automationSettings, setAutomationSettings] = useState({
    createJournalEntry: false,
    createExpense: false,
    salaryAccount: '5100',
    commissionAccount: '5200',
    cashAccount: '1000',
    expenseCategory: 'payroll'
  });

  // Load automation settings from company settings (read-only for display)
  useEffect(() => {
    const loadSettings = async () => {
      if (currentCompany?.id) {
        try {
          const { companySettingsService } = await import('../../../services/system/company-settings');
          const settings = await companySettingsService.getCompanySettings(currentCompany.id);
          
          if (settings?.payrollSettings) {
            setAutomationSettings({
              createJournalEntry: settings.payrollSettings.defaultCreateJournalEntry ?? false,
              createExpense: settings.payrollSettings.defaultCreateExpense ?? false,
              salaryAccount: settings.payrollSettings.defaultSalaryAccount || '5100',
              commissionAccount: settings.payrollSettings.defaultCommissionAccount || '5200',
              cashAccount: settings.payrollSettings.defaultCashAccount || '1000',
              expenseCategory: settings.payrollSettings.defaultExpenseCategory || 'payroll'
            });
          }
        } catch (error) {
          console.warn('Could not load payroll settings:', error);
        }
      }
    };

    if (isOpen) {
      loadSettings();
    }
  }, [isOpen, currentCompany?.id]);

  const loadPreviewData = useCallback(async () => {
    try {
      if (!period || !period.startDate || !period.endDate || !currentCompany?.id) {
        console.log('Period data is incomplete or missing:', { period, companyId: currentCompany?.id });
        return;
      }
      
      setLoading(true);
      
      // Get employees
      const employeesData = await payrollService.getEmployees(currentCompany.id);
      
      // Calculate preview totals
      const startDate = new Date(period.startDate);
      const endDate = new Date(period.endDate);
      
      let totalSalaries = 0;
      let totalCommissions = 0;
      const employeePreview = [];
      
      for (const employee of employeesData) {
        const salary = employee.salary || 0;
        let commission = 0;
        
        // Calculate commissions for drivers (always calculate for display, but may not include in total)
        if (employee.type === 'driver' && employee.commissionEligible) {
          const commissionData = await payrollService.calculateDriverCommissions(
            employee.id,
            currentCompany.id,
            startDate,
            endDate
          );
          commission = commissionData.unpaidCommissions || 0;
        }
        
        const totalDue = salary + commission;
        
        totalSalaries += salary;
        totalCommissions += commission;
        
        employeePreview.push({
          ...employee,
          salary,
          commission,
          totalDue
        });
      }
      
      setPreview({
        totalSalaries,
        totalCommissions,
        totalPayable: totalSalaries + totalCommissions,
        employeeCount: employeesData.length,
        employees: employeePreview
      });
      
    } catch (error) {
      console.error('Error loading preview data:', error);
      toast.error(t('payroll.error.preview_failed', 'Failed to load payroll preview'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, period, t]);

  // Load preview data when modal opens
  useEffect(() => {
    if (isOpen && period && currentCompany?.id) {
      loadPreviewData();
    }
  }, [isOpen, period, currentCompany?.id, loadPreviewData]);

  const handleProcess = async () => {
    if (!period || !currentCompany?.id) return;
    
    try {
      setStep('processing');
      setLoading(true);
      
      await payrollService.processPayrollAccrual(
        currentCompany.id,
        period.id,
        currentUser?.uid || 'system'
      );
      
      toast.success(t('payroll.success.payroll_processed', 'Payroll processed successfully'));
      onSuccess();
      
    } catch (error) {
      console.error('Error processing payroll:', error);
      toast.error(t('payroll.error.processing_failed', 'Failed to process payroll'));
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('preview');
    setPreview(null);
    onClose();
  };

  if (!isOpen || !period) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('payroll.process_payroll', 'Process Payroll')} - {period.year}-{String(period.month).padStart(2, '0')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Preview Step */}
          {step === 'preview' && (
            <div className="p-6 space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3">{t('common.loading', t('common.loading'))}</span>
                </div>
              ) : preview ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">{preview.employeeCount}</div>
                      <div className="text-sm text-blue-700">{t('payroll.employees', 'Employees')}</div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">{formatCurrency(preview.totalSalaries)}</div>
                      <div className="text-sm text-green-700">{t('payroll.total_salaries', 'Total Salaries')}</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">{formatCurrency(preview.totalCommissions)}</div>
                      <div className="text-sm text-purple-700">{t('payroll.total_commissions', 'Total Commissions')}</div>
                      <div className="text-xs text-purple-600 mt-1">
                        {preview.employees.filter(e => e.commission > 0).length} {t('payroll.employees', 'employees')}
                      </div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-orange-600">{formatCurrency(preview.totalPayable)}</div>
                      <div className="text-sm text-orange-700">{t('payroll.total_payable', 'Total Payable')}</div>
                      <div className="text-xs text-orange-600 mt-1">
                        {t('payroll.salary', 'Salary')} + {t('payroll.commission', 'Commission')}
                      </div>
                    </div>
                  </div>

                  {/* Employee Details */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {t('payroll.employee_breakdown', 'Employee Breakdown')}
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>{t('payroll.employee_name', 'Employee Name')}</th>
                            <th>{t('payroll.role', 'Role')}</th>
                            <th>{t('payroll.type', 'Type')}</th>
                            <th>{t('payroll.salary', 'Salary')}</th>
                            <th>{t('payroll.commission', 'Commission')}</th>
                            <th>{t('payroll.total_due', 'Total Due')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.employees.map(employee => (
                            <tr key={employee.id} className={employee.commission > 0 ? 'bg-purple-50' : ''}>
                              <td className="font-medium">{employee.name}</td>
                              <td>{employee.role}</td>
                              <td>
                                <span className={`badge ${employee.type === 'driver' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                                  {employee.type === 'driver' ? t('payroll.driver', 'Driver') : t('payroll.employee', 'Employee')}
                                </span>
                              </td>
                              <td>{formatCurrency(employee.salary)}</td>
                              <td>
                                <div className="flex flex-col">
                                  <span className={`font-medium ${employee.commission > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                                    {formatCurrency(employee.commission)}
                                  </span>
                                  {employee.commission > 0 && employee.commissionEligible && (
                                    <span className="text-xs text-purple-500">
                                      {employee.commissionRate}% {t('payroll.commission_rate', 'rate')}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="font-medium text-lg">{formatCurrency(employee.totalDue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Automation Settings (Read-only from Company Settings) */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-blue-900">
                        {t('payroll.automation.title', 'Automation Settings')}
                      </h3>
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                        {t('settings.configured_in_settings', 'Configured in Settings')}
                      </span>
                    </div>
                    <p className="text-sm text-blue-700 mb-4">
                      {t('payroll.automation.settings_description', 'These settings are configured in Accounting Settings and will be applied automatically.')}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Current Automation Configuration */}
                      <div>
                        <h4 className="font-medium text-blue-900 mb-3">{t('payroll.automation.current_config', 'Current Configuration')}</h4>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <div className={`w-3 h-3 rounded-full mr-2 ${automationSettings.createJournalEntry ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            <span className="text-sm text-blue-800">
                              {t('payroll.automation.create_journal_entry', 'Create Journal Entry')}
                            </span>
                          </div>
                          
                          <div className="flex items-center">
                            <div className={`w-3 h-3 rounded-full mr-2 ${automationSettings.createExpense ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            <span className="text-sm text-blue-800">
                              {t('payroll.automation.create_expense', 'Create Expense Records')}
                            </span>
                          </div>
                          
                          {!automationSettings.createJournalEntry && !automationSettings.createExpense && (
                            <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                              {t('payroll.automation.no_automation', 'No automation configured - only payroll entries will be created')}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Account Mapping (if automation is enabled) */}
                      {(automationSettings.createJournalEntry || automationSettings.createExpense) && (
                        <div>
                          <h4 className="font-medium text-blue-900 mb-3">{t('payroll.automation.account_mapping', 'Account Mapping')}</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-blue-700">{t('payroll.automation.salary_account', 'Salary Account')}:</span>
                              <span className="font-mono text-blue-900">{automationSettings.salaryAccount}</span>
                            </div>
                            
                            <div className="flex justify-between">
                              <span className="text-blue-700">{t('payroll.automation.commission_account', 'Commission Account')}:</span>
                              <span className="font-mono text-blue-900">{automationSettings.commissionAccount}</span>
                            </div>
                            
                            <div className="flex justify-between">
                              <span className="text-blue-700">{t('payroll.automation.cash_account', 'Cash Account')}:</span>
                              <span className="font-mono text-blue-900">{automationSettings.cashAccount}</span>
                            </div>
                            
                            {automationSettings.createExpense && (
                              <div className="flex justify-between">
                                <span className="text-blue-700">{t('payroll.automation.expense_category', 'Expense Category')}:</span>
                                <span className="font-medium text-blue-900 capitalize">{automationSettings.expenseCategory}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4 text-xs text-blue-600">
                      {t('payroll.automation.change_settings_note', 'To change these settings, go to Settings → Payroll Automation')}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* Confirm Step */}
          {step === 'confirm' && preview && (
            <div className="p-6 space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 mt-0.5 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-yellow-900">
                      {t('payroll.confirm_processing', 'Confirm Payroll Processing')}
                    </h3>
                    <p className="text-sm text-yellow-700 mt-2">
                      {t('payroll.processing_warning', 'This will create payroll entries and journal entries for all employees. This action cannot be undone.')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{t('payroll.processing_summary', 'Processing Summary')}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span>{t('payroll.employees_to_process', 'Employees to Process')}:</span>
                    <span className="font-medium">{preview.employeeCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('payroll.total_salaries', 'Total Salaries')}:</span>
                    <span className="font-medium">{formatCurrency(preview.totalSalaries)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('payroll.total_commissions', 'Total Commissions')}:</span>
                    <span className="font-medium">{formatCurrency(preview.totalCommissions)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('payroll.total_payable', 'Total Payable')}:</span>
                    <span className="font-medium text-lg">{formatCurrency(preview.totalPayable)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Processing Step */}
          {step === 'processing' && (
            <div className="p-6">
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">{t('payroll.processing', 'Processing Payroll...')}</h3>
                  <p className="text-sm text-gray-500">{t('payroll.processing_description', 'Creating payroll entries and journal entries')}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          {step === 'preview' && (
            <>
              <button
                onClick={handleClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                {t('common.cancel', t('common.cancel'))}
              </button>
              <button
                onClick={() => setStep('confirm')}
                className="btn btn-primary"
                disabled={loading || !preview}
              >
                <PlayIcon className="w-4 h-4 mr-2" />
                {t('payroll.continue', 'Continue')}
              </button>
            </>
          )}
          
          {step === 'confirm' && (
            <>
              <button
                onClick={() => setStep('preview')}
                className="btn btn-secondary"
                disabled={loading}
              >
                {t('common.back', 'Back')}
              </button>
              <button
                onClick={handleProcess}
                className="btn btn-primary"
                disabled={loading}
              >
                <PlayIcon className="w-4 h-4 mr-2" />
                {t('payroll.process_now', 'Process Now')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcessPayrollModal;